import { useMemo } from 'react';
import { getGamesByCategory, getLatestGames, type Game } from '@lib/content';
import { GAME_LIMITS } from '../config';

export interface GameRecommendations {
  sidebarGames: Game[];
  moreGames: Game[];
}

/**
 * Get series/version games for current game
 */
function getSeriesGames(currentGame: Game, allCategoryGames: Game[]): Game[] {
  const currentTitle = currentGame.title.toLowerCase();
  const currentSlug = currentGame.slug.toLowerCase();

  // Extract base name from title (remove numbers and common words)
  const getBaseName = (title: string) => {
    return title
      .toLowerCase()
      .replace(/\b(1|2|3|4|5|6|7|8|9|10|i+|ii+|iii+|iv+|v+)\b/g, '')
      .replace(/\b(epic|battle|new|super|mega|ultra|pro|plus|deluxe|winter|spooky|pool|party)\b/g, '')
      .replace(/[-:\s]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  };

  const getBaseSlug = (slug: string) => {
    return slug
      .replace(/-(\d+)$/, '') // Remove trailing numbers
      .replace(/-(epic|battle|new|super|mega|ultra|pro|plus|deluxe|winter|spooky|pool|party)(-\d+)?$/, '')
      .replace(/-(1|2|3|4|5|6|7|8|9|10)(-\w+)*$/, '');
  };

  const currentBaseName = getBaseName(currentGame.title);
  const currentBaseSlug = getBaseSlug(currentGame.slug);

  // Find series games
  const seriesGames = allCategoryGames.filter(game => {
    if (game.slug === currentGame.slug) return false;

    const gameBaseName = getBaseName(game.title);
    const gameBaseSlug = getBaseSlug(game.slug);

    // Check if same series by title or slug
    return (currentBaseName === gameBaseName && currentBaseName.length > 3) ||
      (currentBaseSlug === gameBaseSlug && currentBaseSlug.length > 3) ||
      // Manual series mappings for common cases
      isInSameSeries(currentGame.slug, game.slug);
  });

  // Sort by version/number (try to get sequential order)
  return seriesGames.sort((a, b) => {
    const aNum = extractNumber(a.title) || extractNumber(a.slug) || 0;
    const bNum = extractNumber(b.title) || extractNumber(b.slug) || 0;
    return aNum - bNum;
  });
}

/**
 * Extract number from title/slug for version ordering
 */
function extractNumber(text: string): number | null {
  const match = text.match(/\b(\d+)\b/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Manual series detection for common game series (based on actual games in project)
 */
function isInSameSeries(slug1: string, slug2: string): boolean {
  const seriesMap: Record<string, string[]> = {
    // Pokemon Series
    'pokemon-emerald': ['pokemon-emerald-2', 'pokemon-firered', 'pokemondiamond', 'pokemonplatinum'],
    'pokemon-emerald-2': ['pokemon-emerald', 'pokemon-firered', 'pokemondiamond', 'pokemonplatinum'],
    'pokemon-firered': ['pokemon-emerald', 'pokemon-emerald-2', 'pokemondiamond', 'pokemonplatinum'],
    'pokemondiamond': ['pokemon-emerald', 'pokemon-emerald-2', 'pokemon-firered', 'pokemonplatinum'],
    'pokemonplatinum': ['pokemon-emerald', 'pokemon-emerald-2', 'pokemon-firered', 'pokemondiamond'],

    // Epic Battle Fantasy Series
    'epicbattlefantasy': ['epicbattlefantasy2', 'epicbattlefantasy3'],
    'epicbattlefantasy2': ['epicbattlefantasy', 'epicbattlefantasy3'],
    'epicbattlefantasy3': ['epicbattlefantasy', 'epicbattlefantasy2'],

    // Stickman Fighter Series
    'stickman-fighter-epic-battle': ['stickman-fighter-epic-battle-2', 'stickman-fighter-mega-brawl'],
    'stickman-fighter-epic-battle-2': ['stickman-fighter-epic-battle', 'stickman-fighter-mega-brawl'],
    'stickman-fighter-mega-brawl': ['stickman-fighter-epic-battle', 'stickman-fighter-epic-battle-2'],

    // Basketball Series
    'basketball-legends': ['basketball-legends2020', 'basketball-serial-shooter', 'street-basketball-1'],
    'basketball-legends2020': ['basketball-legends', 'basketball-serial-shooter', 'street-basketball-1'],
    'basketball-serial-shooter': ['basketball-legends', 'basketball-legends2020', 'street-basketball-1'],
    'street-basketball-1': ['basketball-legends', 'basketball-legends2020', 'basketball-serial-shooter'],

    // Soccer / Football Series
    'football-legends': ['soccer-legends', 'heads-arena-euro-soccer'],
    'soccer-legends': ['football-legends', 'heads-arena-euro-soccer'],
    'heads-arena-euro-soccer': ['football-legends', 'soccer-legends'],

    // Mario Series
    'super-mario-bros': ['super-mario-64', 'super-mario-flash', 'super-mario-wonder', 'supermariobros2', 'supermariobros3', 'supermarioconstruct', 'supermarioflash2', 'supermariokart', 'supermarioworld', 'new-super-mario-bros', 'super-mario-world-online', 'mariocombat', 'cat-mario', 'drmario'],
    'super-mario-64': ['super-mario-bros', 'super-mario-flash', 'super-mario-wonder', 'supermariobros2', 'supermariobros3', 'supermarioconstruct', 'supermarioflash2', 'supermariokart', 'supermarioworld', 'new-super-mario-bros', 'super-mario-world-online', 'mariocombat', 'cat-mario', 'drmario'],
    'super-mario-flash': ['super-mario-bros', 'super-mario-64', 'super-mario-wonder', 'supermariobros2', 'supermariobros3', 'supermarioconstruct', 'supermarioflash2', 'supermariokart', 'supermarioworld', 'new-super-mario-bros', 'super-mario-world-online', 'mariocombat', 'cat-mario', 'drmario'],
    'super-mario-wonder': ['super-mario-bros', 'super-mario-64', 'super-mario-flash', 'supermariobros2', 'supermariobros3', 'supermarioconstruct', 'supermarioflash2', 'supermariokart', 'supermarioworld', 'new-super-mario-bros', 'super-mario-world-online', 'mariocombat', 'cat-mario', 'drmario'],

    // Monster Truck Series
    'monster-truck-racing-arena': ['monster-truck-vs-zombie', 'real-simulator-monster-truck', 'racing-monster-truck', 'monsters-wheels-special', '3d-monster-truck-skyroads', 'impossible-monster-truck', 'brain-for-monster-truck'],
    'monster-truck-vs-zombie': ['monster-truck-racing-arena', 'real-simulator-monster-truck', 'racing-monster-truck', 'monsters-wheels-special', '3d-monster-truck-skyroads', 'impossible-monster-truck', 'brain-for-monster-truck'],
    'real-simulator-monster-truck': ['monster-truck-racing-arena', 'monster-truck-vs-zombie', 'racing-monster-truck', 'monsters-wheels-special', '3d-monster-truck-skyroads', 'impossible-monster-truck', 'brain-for-monster-truck'],

    // Zombie Series
    'zombie-clash-3d': ['zombie-outbreak-arena', 'zombie-shooter-2d', 'zombies-shooter-part-2', 'zombie-derby-pixel-survival'],
    'zombie-outbreak-arena': ['zombie-clash-3d', 'zombie-shooter-2d', 'zombies-shooter-part-2', 'zombie-derby-pixel-survival'],
    'zombie-shooter-2d': ['zombie-clash-3d', 'zombie-outbreak-arena', 'zombies-shooter-part-2', 'zombie-derby-pixel-survival'],
    'zombies-shooter-part-2': ['zombie-clash-3d', 'zombie-outbreak-arena', 'zombie-shooter-2d', 'zombie-derby-pixel-survival'],

    // Tower Defense Series
    'bloons-tower-defense': ['bloons-tower-defense-3', 'bloons-tower-defense-4', 'balloon-tower-defense-5'],
    'bloons-tower-defense-3': ['bloons-tower-defense', 'bloons-tower-defense-4', 'balloon-tower-defense-5'],
    'bloons-tower-defense-4': ['bloons-tower-defense', 'bloons-tower-defense-3', 'balloon-tower-defense-5'],
    'balloon-tower-defense-5': ['bloons-tower-defense', 'bloons-tower-defense-3', 'bloons-tower-defense-4'],

    // Battle Simulator Series
    'funny-battle-simulator': ['funny-battle-simulator-2', 'squid-battle-simulator'],
    'funny-battle-simulator-2': ['funny-battle-simulator', 'squid-battle-simulator'],
    'squid-battle-simulator': ['funny-battle-simulator', 'funny-battle-simulator-2'],

    // Clash Series
    'ninja-clash-heroes': ['farm-clash-3d', 'zombie-clash-3d'],
    'farm-clash-3d': ['ninja-clash-heroes', 'zombie-clash-3d'],

    // Monster / Merge Series
    'merge-monster-army-game': ['dinosaurs-merge-master', 'pudding-monsters'],
    'dinosaurs-merge-master': ['merge-monster-army-game', 'pudding-monsters'],
    'pudding-monsters': ['merge-monster-army-game', 'dinosaurs-merge-master'],

    // Minecraft Style
    'herobrine-vs-monster-school': ['monster-school-vs-siren-head', 'red-stickman-vs-monster-school', 'minecrafttowerdefence'],
    'monster-school-vs-siren-head': ['herobrine-vs-monster-school', 'red-stickman-vs-monster-school', 'minecrafttowerdefence'],
    'red-stickman-vs-monster-school': ['herobrine-vs-monster-school', 'monster-school-vs-siren-head', 'minecrafttowerdefence'],

    // Roguelike / Similar
    'pokerogue': ['super-auto-pets', 'epicbattlefantasy', 'epicbattlefantasy2', 'epicbattlefantasy3'],
    'super-auto-pets': ['pokerogue']
  };

  return seriesMap[slug1]?.includes(slug2) ?? false;
}

/**
 * Enhanced game recommendations with series/version priority
 * Priority: Featured games > Series games > Same category games > Latest games
 */
export function useGameRecommendations(game: Game | null): GameRecommendations {
  return useMemo(() => {
    if (!game) return { sidebarGames: [], moreGames: [] };

    // Define priority games for homepage sidebar based on new categories
    const priorityGames = [
      'pokemon-emerald',
      'pokemon-emerald-2',
      'pokemon-firered',
      'pokemondiamond',
      'pokemonplatinum',
      'epicbattlefantasy',
      'epicbattlefantasy2',
      'epicbattlefantasy3',
      'super-auto-pets',
      'merge-monster-army-game',
      'dinosaurs-merge-master',
      'monster-school-vs-siren-head',
      'red-stickman-vs-monster-school',
      'dragon-simulator-3d',
      'grow-your-garden'
    ];

    // Get all games from all categories of current game
    const allCategories = game.categories || [];
    const allRelatedGames: Game[] = [];
    const usedSlugs = new Set<string>([game.slug]);

    // Collect games from all categories (not just primary)
    for (const category of allCategories) {
      const categoryGames = getGamesByCategory(category).filter((g) => !usedSlugs.has(g.slug));
      allRelatedGames.push(...categoryGames);
      categoryGames.forEach(g => usedSlugs.add(g.slug));
    }

    // PRIORITY 1: Get series/version games first
    const seriesGames = getSeriesGames(game, allRelatedGames);

    // PRIORITY 2: Get priority games from the current game's categories
    const priorityRelatedGames = allRelatedGames.filter(g =>
      priorityGames.includes(g.slug) && !seriesGames.some(sg => sg.slug === g.slug)
    ).sort((a, b) => {
      const aIndex = priorityGames.indexOf(a.slug);
      const bIndex = priorityGames.indexOf(b.slug);
      return aIndex - bIndex;
    });

    // PRIORITY 3: Get remaining category games (excluding series and priority games)
    const remainingCategoryGames = allRelatedGames.filter(g =>
      !seriesGames.some(sg => sg.slug === g.slug) &&
      !priorityRelatedGames.some(pg => pg.slug === g.slug)
    );

    // PRIORITY 4: Get latest games as fallback
    const latestGames = getLatestGames(GAME_LIMITS.LATEST_GAMES_POOL)
      .filter((g) => g.slug !== game.slug && !usedSlugs.has(g.slug));

    // Build sidebar with priority order
    const sidebar: Game[] = [];
    const finalUsedSlugs = new Set<string>([game.slug]);

    // Add series games first (highest priority for related games)
    for (const seriesGame of seriesGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(seriesGame.slug)) {
        sidebar.push(seriesGame);
        finalUsedSlugs.add(seriesGame.slug);
      }
    }

    // Add priority games from same categories
    for (const priorityGame of priorityRelatedGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(priorityGame.slug)) {
        sidebar.push(priorityGame);
        finalUsedSlugs.add(priorityGame.slug);
      }
    }

    // Add remaining category games
    for (const categoryGame of remainingCategoryGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(categoryGame.slug)) {
        sidebar.push(categoryGame);
        finalUsedSlugs.add(categoryGame.slug);
      }
    }

    // Fill up with latest games if needed
    for (const latestGame of latestGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(latestGame.slug)) {
        sidebar.push(latestGame);
        finalUsedSlugs.add(latestGame.slug);
      }
    }

    // Build more games section
    const more: Game[] = [];

    // Add remaining series games that didn't fit in sidebar
    for (const seriesGame of seriesGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(seriesGame.slug)) {
        more.push(seriesGame);
        finalUsedSlugs.add(seriesGame.slug);
      }
    }

    // Add remaining priority games
    for (const priorityGame of priorityRelatedGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(priorityGame.slug)) {
        more.push(priorityGame);
        finalUsedSlugs.add(priorityGame.slug);
      }
    }

    // Add remaining category games
    for (const categoryGame of remainingCategoryGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(categoryGame.slug)) {
        more.push(categoryGame);
        finalUsedSlugs.add(categoryGame.slug);
      }
    }

    // Fill up with latest games if needed
    for (const latestGame of latestGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(latestGame.slug)) {
        more.push(latestGame);
        finalUsedSlugs.add(latestGame.slug);
      }
    }

    return { sidebarGames: sidebar, moreGames: more };
  }, [game]);
}
